import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getAllOrganizations } from "../../utils/Api";
import AdminOrgsSkeleton from "../../components/SkeletonLoading/AdminOrgsSkeleton";
import {
  Building2,
  ArrowUp,
  ArrowDown,
  Users,
  Plus,
  Search,
} from "lucide-react";
import SecondaryButton from "../../components/Buttons/SecondaryButton";
import PrimaryButton from "../../components/Buttons/PrimaryButton";
import CreateOrganizationModal from "../../modals/CreateOrganization";

interface Organization {
  id: string;
  name: string;
  member_count: number;
  document_count: number;
  total_pages: number;
  created_at: string;
}

function Organizations() {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [displayedCount, setDisplayedCount] = useState(10);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const fetchOrganizations = async () => {
    try {
      const response = await getAllOrganizations();
      setOrganizations(response.data);
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
      setError("Failed to load organizations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const toggleSortOrder = () => {
    setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const sortedOrganizations = [...filteredOrganizations].sort((a, b) => {
    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
    return sortOrder === "asc" ? dateA - dateB : dateB - dateA;
  });

  const totalOrganizations = sortedOrganizations.length;
  const displayedOrganizations = sortedOrganizations.slice(0, displayedCount);
  const remainingCount = totalOrganizations - displayedCount;

  const handleShowMore = () => {
    setDisplayedCount((prev) => prev + 10);
  };

  const handleShowAll = () => {
    setDisplayedCount(totalOrganizations);
  };

  if (loading) {
    return <AdminOrgsSkeleton />;
  }

  if (error) {
    return (
      <div className="bg-white min-h-full">
        <div className="w-full mx-auto py-10 px-4 sm:px-6 lg:px-10">
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-6 sm:pt-10 px-4 sm:px-6 lg:px-10 flex-shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h3 className="text-xl font-semibold tracking-tight text-gray-900">
              Organisations
            </h3>
            <p className="text-sm text-gray-500 mt-0.5">
              View and manage all organisations
            </p>
          </div>
          <PrimaryButton
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 w-full sm:w-auto justify-center flex-shrink-0"
          >
            <Plus size={14} />
            Create organisation
          </PrimaryButton>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-10 pb-10">
        <div className="mb-4 flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-widest text-gray-400">
            All organisations
          </p>
        </div>

        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="text"
            className="w-full sm:max-w-xs h-9 pl-9 pr-4 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-[#1d365d] focus:border-[#1d365d] transition-colors"
            placeholder="Search organisations..."
            aria-label="Search organisations"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden sm:table-cell">
                  Members
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden sm:table-cell">
                  Documents
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden sm:table-cell">
                  Pages
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-widest text-gray-400 hidden md:table-cell">
                  <button
                    onClick={toggleSortOrder}
                    className="flex items-center gap-1.5 uppercase tracking-widest hover:text-gray-600 transition-colors"
                    title={`Sort by date ${sortOrder === "asc" ? "descending" : "ascending"}`}
                    disabled={filteredOrganizations.length === 0}
                  >
                    Created
                    {sortOrder === "desc" ? (
                      <ArrowDown size={13} />
                    ) : (
                      <ArrowUp size={13} />
                    )}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredOrganizations.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-16 text-center">
                    <Building2 className="w-8 h-8 mx-auto mb-3 text-gray-200" />
                    <p className="text-sm font-medium text-gray-900 mb-1">
                      {searchQuery
                        ? "No organisations found"
                        : "No organisations yet"}
                    </p>
                    <p className="text-sm text-gray-400">
                      {searchQuery
                        ? `No organisations match "${searchQuery}"`
                        : "Organisations will appear here once created"}
                    </p>
                  </td>
                </tr>
              ) : (
                displayedOrganizations.map((org) => (
                  <tr
                    key={org.id}
                    onClick={() =>
                      navigate(`/home/admin/organizations/${org.id}`)
                    }
                    className="group hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0 h-7 w-7 bg-gray-100 rounded-full flex items-center justify-center">
                          <Building2 className="w-3.5 h-3.5 text-gray-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {org.name}
                          </p>
                          <p className="text-xs text-gray-400 sm:hidden flex items-center gap-1 mt-0.5">
                            <Users size={11} />
                            {org.member_count} member
                            {org.member_count !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                      {org.member_count}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                      {org.document_count}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 hidden sm:table-cell">
                      {org.total_pages}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 hidden md:table-cell">
                      {org.created_at
                        ? new Date(org.created_at).toLocaleString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: false,
                          })
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          {filteredOrganizations.length > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/50">
              <p className="text-xs text-gray-400">
                {searchQuery ? (
                  <>
                    Showing{" "}
                    <span className="font-medium text-gray-600">
                      {filteredOrganizations.length}
                    </span>{" "}
                    of{" "}
                    <span className="font-medium text-gray-600">
                      {organizations.length}
                    </span>{" "}
                    organisations
                  </>
                ) : (
                  <>
                    Total organisations:{" "}
                    <span className="font-medium text-gray-600">
                      {organizations.length}
                    </span>
                  </>
                )}
              </p>
            </div>
          )}
        </div>

        {remainingCount > 0 && (
          <div className="mt-4 text-center">
            <SecondaryButton
              onClick={handleShowMore}
              className="mb-2 w-full sm:w-auto"
            >
              Show {Math.min(remainingCount, 10)} more organisations
            </SecondaryButton>
            <p className="text-xs text-gray-400">
              Showing {displayedCount} of {totalOrganizations}.{" "}
              <button
                onClick={handleShowAll}
                className="text-[#1d365d] hover:text-[#2d4a75] font-medium transition-colors"
              >
                Show all
              </button>
            </p>
          </div>
        )}
      </div>

      <CreateOrganizationModal
        isOpen={isCreateModalOpen}
        setIsOpen={setIsCreateModalOpen}
        onSuccess={fetchOrganizations}
      />
    </div>
  );
}

export default Organizations;
